import { LOGIN_PATH, OIDC_CALLBACK_PATH } from "@airange/shared/oidc-routes";
import { resolveCtfAdminPageTitleId } from "./page-title-rules";

export type CtfBreadcrumbItem = {
  path: string;
  breadcrumbName: string;
};

type BreadcrumbLabelResolver = (descriptor: { id: string }) => string;

type BreadcrumbTemplate = {
  match: RegExp;
  items: Array<{
    path: string;
    labelId: string;
  }>;
};

const CTF_BREADCRUMB_TEMPLATES: BreadcrumbTemplate[] = [
  {
    match: /^\/dashboard$/,
    items: [{ path: "/dashboard", labelId: "ctf.pages.dashboard.title" }],
  },
  {
    match: /^\/categories$/,
    items: [{ path: "/categories", labelId: "ctf.pages.categories.title" }],
  },
  {
    match: /^\/challenges$/,
    items: [{ path: "/challenges", labelId: "ctf.pages.challenges.title" }],
  },
  {
    match: /^\/artifact-releases$/,
    items: [{ path: "/artifact-releases", labelId: "ctf.pages.artifactReleases.title" }],
  },
];

function isBreadcrumbDisabledPath(pathname: string) {
  return pathname === LOGIN_PATH || pathname === OIDC_CALLBACK_PATH;
}

function resolveBreadcrumbTemplate(pathname: string) {
  return CTF_BREADCRUMB_TEMPLATES.find((template) => template.match.test(pathname)) ?? null;
}

export function buildCtfAdminBreadcrumbs(
  pathname: string,
  resolveLabel: BreadcrumbLabelResolver,
): CtfBreadcrumbItem[] {
  if (isBreadcrumbDisabledPath(pathname)) {
    return [];
  }

  const rootItem: CtfBreadcrumbItem = {
    path: "/",
    breadcrumbName: resolveLabel({ id: "ctf.shell.title" }),
  };

  const template = resolveBreadcrumbTemplate(pathname);
  if (template) {
    return [
      rootItem,
      ...template.items.map((item) => ({
        path: item.path,
        breadcrumbName: resolveLabel({ id: item.labelId }),
      })),
    ];
  }

  const fallbackLabelId = resolveCtfAdminPageTitleId(pathname);
  if (!fallbackLabelId) {
    return [];
  }

  return [
    rootItem,
    {
      path: pathname,
      breadcrumbName: resolveLabel({ id: fallbackLabelId }),
    },
  ];
}
